/**
 * @play/catalog — the pinned Runebound catalogue and everything derived from it (docs/design/01 §3,
 * 07 §4-5): card/printing/set lookups, the alias map (variant ids → rules identities), token display
 * data, art URLs (R2 mirror + Riot CDN), the plain-printing rule, deck normalisation and play's own deck
 * validator (owner decision Q15).
 */
import { type Catalog, createCatalog } from './catalog';
import { CATALOG_VERSION, loadPinnedCatalogData } from './pinned';

export * from './schema';
export { Catalog, catalogFromTrustedData, createCatalog } from './catalog';
export { CATALOG_VERSION, loadPinnedCatalogData } from './pinned';
export { computeAliases, type AliasInput, type AliasResult } from './aliases';
export { TOKEN_ROW_SUFFIX, VARIANT_SUFFIX, displayCardName, foldText, normaliseCardName, rulesName } from './names';
export {
  decodeEntities,
  hasRulesText,
  isUniqueText,
  oracleText,
  rulesBlocks,
  textHash,
  unlimitedCopiesName,
  type RulesBlock,
} from './text';
export {
  ART_WIDTHS,
  DEFAULT_ART_CONFIG,
  artSources,
  isLandscapeImage,
  mirrorArtUrl,
  pickArtWidth,
  riotArtUrl,
  type ArtConfig,
  type ArtWidth,
} from './art';
export { TOKEN_DISPLAY, tokenDisplay, type TokenDisplay } from './tokens';
export { printedCard, printedCards } from './printed';
export { comparePlainness, plainestPrinting } from './printings';
export { type DeckIssue, type DeckIssueCode, type DeckSection, issue } from './deck/issues';
export { FORMATS, STANDARD, type FormatId, type FormatRules } from './deck/formats';
export { BANNED_STANDARD, resolveBanList, type BanList } from './deck/bans';
export { deckHash } from './deck/hash';
export {
  RUNEBOUND_DECK_ZONES,
  RuneboundDeckSchema,
  normaliseDeck,
  type NormalisedDeck,
  type RuneboundDeck,
} from './deck/normalise';
export {
  championTag,
  legendDomains,
  toRulesCard,
  toRulesDeck,
  validateDeck,
  type DeckCounts,
  type DeckValidation,
  type ValidateOptions,
} from './deck/validate';

let pinned: Promise<Catalog> | undefined;

/** The pinned catalogue, loaded and validated once per process (lazy). */
export function loadPinnedCatalog(): Promise<Catalog> {
  pinned ??= loadPinnedCatalogData().then((data) => {
    const catalog = createCatalog(data);
    if (catalog.version !== CATALOG_VERSION)
      throw new Error(`Pinned catalogue ${CATALOG_VERSION} contains ${catalog.version}`);
    return catalog;
  });
  return pinned;
}
